import { Injectable } from '@angular/core';
import { API } from './api';
import { BaseProvider } from './base';
import { JobProvider, JobType, JobResponse, JobState, JobEventData, TypeToJobMap, JobsEventData } from './job';
import { Modal } from 'ionic-angular';
import { APIUsageProvider } from './api_usage';
import { ModalProvider } from './popup/modal';
import { Comprehensiveness, OptimizeFor, DetectionOptimizationType } from './optimization-runs';
import { EventName, Events } from '../utils/events';
import { DatasetBrain } from './dataset';
import { BrainBankErrorCode } from './error-code';
import { AuthProvider } from './auth';
import { Subscription } from 'rxjs';
import { TrackableJobProvider } from './trackable-job';
import { TranslateService } from '@ngx-translate/core';

const BOOST_JOB_TYPES = [JobType.Retrain, JobType.Optimize, JobType.Freeze, JobType.Train, JobType.AnomalyHiFi];

export const STATE_DESCRIPTION_RECALCULATE = 'Recalculating Accuracy';

// It's up to the job tracking frontend components to interpret the state information from the boost provider.
// The expected state flow is
// * OUT_OF_SYNC/READY -> WAITING -> IN_PROGRESS -> RECALCULATE -> DONE/ERROR,
// or
// * OUT_OF_SYNC/READY -> WAITING -> IN_PROGRESS -> RECALCULATE -> CANCELING -> CANCELED.
export enum BoostJobUXState {
    OUT_OF_SYNC = 'OutOfSyncWarning', // Exclusively used for boosting; similar to READY

    // This corresponds to the front end state where a user can configure and then
    // press a button to launch a job. This does NOT correspond to the back end job
    // Ready state
    READY = 'Ready',

    WAITING = 'Waiting',
    IN_PROGRESS = 'In Progress', // Training
    RECALCULATE = 'Recalculating Accuracy', // Formerly included in IN_PROGRESS
    DONE = 'Done',
    ERROR = 'Error',
    CANCELING = 'Canceling', // Cancel Requested and Canceling
    CANCELED = 'Canceled'
}

export const MOCK_BOOST_ESTIMATE_MULTIPLIER = 0.1; // Represents 100 milliseconds

export enum Platform {
    Linux_x86_CUDA = 'Linux-x86-CUDA',
    Linux_x86_CPU = 'Linux-x86-CPU',
    iOS = 'iOS',
    Android = 'Android'
}

export enum OptimizeTag {
    // Used for Scene/Anomaly Recognition Learning optimization
    Small = 'Small',
    Medium = 'Medium',
    Large = 'Large',
    Explainability = 'Explainability',

    // Used for FLIR account tiers
    Firefly = 'Firefly',
    Firefly30 = 'Firefly30',
    Firefly45 = 'Firefly45',
    Firefly60 = 'Firefly60',

    // Used for HiFi training
    HiFiCoarse = 'HiFiCoarse',
    HiFiRegular = 'HiFiRegular',
    HiFiFine = 'HiFiFine',
    HiFiUltraFine = 'HiFiUltraFine',

    // If set, tells BrainBank to re-use the backbone from the current brainVersion
    Reuse = 'Reuse'
}

export interface OptimizationLevers {
    comprehensiveness: Comprehensiveness;
    requiredPlatforms: Platform[] | null;
    optimizeFor: OptimizeFor;
    tag: OptimizeTag | null;
}

export interface DetectionTrainLevers {
    optimizationType: DetectionOptimizationType;
    sendEmail: boolean;
}

export interface JobCancelResponse {
    success: boolean;
}

export interface LatestEstimate {
    rawTime: number | undefined;
    currentStep: number;
    totalSteps: number;
}

export enum HiFiDefectSizing {
    OptimizeForDataset = 'OptimizeForDataset',
    Coarse = 'Coarse',
    Regular = 'Regular',
    Fine = 'Fine',
    UltraFine = 'UltraFine'
}

@Injectable()
export class BoostProvider extends BaseProvider {
    private retrainProgressModal: Modal | undefined; // For retrain only, not other boost job types

    constructor(
        public api: API,
        private jobProvider: JobProvider,
        private trackableJobProvider: TrackableJobProvider,
        private modalProvider: ModalProvider,
        private apiUsageProvider: APIUsageProvider,
        private translate: TranslateService
    ) {
        super();
    }

    /** NOTE: We used to return out of the retrain/optimize/freeze/trainDetection functions if the brain was currently busy.
     * For the time being, we want to explicitly trigger and display a "DATASET_BUSY" error from the backend if that happens.
     */

    /**
     * Retrain the specified dataset
     *
     * @param datasetIdentifier Unique identifier for the dataset
     * @param sendEmail Specify true to send an email to the user when the retrain job completes
     */
    public async retrain(datasetIdentifier: string, sendEmail: boolean = false): Promise<void> {
        try {
            await this.api.post(`datasets/${datasetIdentifier}/train/retrain`, {
                sendEmail
            });
        } catch (error) {
            this.trackableJobProvider.notifyJobRequestFailed(datasetIdentifier, JobType.Retrain);

            throw error;
        }

        await this.trackableJobProvider.restartJobPolling();
    }

    /**
     * Optimize the specified dataset
     *
     * @param datasetIdentifier Unique identifier for the dataset
     * @param optimizationLevers Execution parameters for the optimization job
     */
    public async optimize(datasetIdentifier: string, optimizationLevers: OptimizationLevers): Promise<void> {
        try {
            await this.api.post(`datasets/${datasetIdentifier}/train/optimize`, optimizationLevers);
        } catch (error) {
            this.trackableJobProvider.notifyJobRequestFailed(datasetIdentifier, JobType.Optimize);

            throw error;
        }

        await this.trackableJobProvider.restartJobPolling();
    }

    /**
     * Freeze the specified dataset
     *
     * @param datasetIdentifier Unique identifier for the dataset
     * @param optimizationLevers Execution parameters for the freeze job
     */
    public async freeze(datasetIdentifier: string, optimizationLevers: OptimizationLevers): Promise<void> {
        try {
            await this.api.post(`datasets/${datasetIdentifier}/train/freeze`, optimizationLevers);
        } catch (error) {
            this.trackableJobProvider.notifyJobRequestFailed(datasetIdentifier, JobType.Freeze);

            throw error;
        }

        await this.trackableJobProvider.restartJobPolling();
    }

    /**
     * Run detection train for the specified dataset
     *
     * @param datasetIdentifier Unique identifier for the dataset
     * @param detectionTrainLevers Execution parameters for the detection train job
     */
    public async trainDetection(datasetIdentifier: string, detectionTrainLevers: DetectionTrainLevers): Promise<void> {
        try {
            await this.api.post(`datasets/${datasetIdentifier}/train/detection`, detectionTrainLevers);
        } catch (error) {
            this.trackableJobProvider.notifyJobRequestFailed(datasetIdentifier, JobType.Train);

            throw error;
        }

        await this.trackableJobProvider.restartJobPolling();
    }

    /**
     * Run anomaly hi-fi training for the specified dataset
     *
     * @param datasetIdentifier Unique identifier for the dataset
     * @param optimizationLevers Execution parameters for the anomaly hi-fi job
     */
    public async buildAnomalyHiFi(datasetIdentifier: string, optimizationLevers: OptimizationLevers): Promise<void> {
        try {
            await this.api.post(`datasets/${datasetIdentifier}/train/anomalyhifi`, optimizationLevers);
        } catch (error) {
            this.trackableJobProvider.notifyJobRequestFailed(datasetIdentifier, JobType.AnomalyHiFi);
            throw error;
        }

        await this.trackableJobProvider.restartJobPolling();
    }

    /**
     * Display the RetrainProgressModal indicating whether the dataset needs to be retrained,
     * is currently retraining or is in a clean state.
     *
     * @param datasetIdentifier Unique identifier for the dataset
     * @param fromOutOfSyncWarning True to indicate that we are displaying the modal as a result
     * of the user taking action on a brain-out-of-sync warning
     */
    public showRetrainProgress(datasetIdentifier: string, fromOutOfSyncWarning: boolean = false): void {
        // If we already have a retrain modal open, don't create a new one.
        // For this to work, we need to explictly clean up the modal when it's closed.
        if (this.modalProvider.modalIsOpen('RetrainProgressModal')) {
            return;
        }

        this.retrainProgressModal = this.modalProvider.createModal(
            'RetrainProgressModal',
            {
                datasetIdentifier,
                continueWithRetrain: this.continueWithRetrain.bind(this),
                fromOutOfSyncWarning
            },
            { cssClass: 'retrain-progress', enableBackdropDismiss: false }
        );

        this.retrainProgressModal.present();
    }

    private async continueWithRetrain(sendEmail: boolean, datasetIdentifier: string): Promise<void> {
        try {
            await this.retrain(datasetIdentifier, sendEmail);
        } catch (err) {
            if (err && err.error && err.error.code == BrainBankErrorCode.DatasetBusy && this.retrainProgressModal) {
                this.retrainProgressModal.dismiss();
                this.retrainProgressModal = undefined;
            }

            throw err;
        }
    }

    public async cancelRetrain(datasetIdentifier: string): Promise<JobCancelResponse> {
        try {
            const cancelResponse = await this.api.post(`datasets/${datasetIdentifier}/train/retrain/cancel`);

            return {
                success: cancelResponse ? cancelResponse.success : false
            };
        } catch (error) {
            throw error;
        }
    }

    public async cancelOptimization(datasetIdentifier: string): Promise<JobCancelResponse> {
        try {
            const cancelResponse = await this.api.post(`datasets/${datasetIdentifier}/train/optimize/cancel`);

            return {
                success: cancelResponse ? cancelResponse.success : false
            };
        } catch (error) {
            throw error;
        }
    }

    public async cancelFreeze(datasetIdentifier: string): Promise<JobCancelResponse> {
        try {
            const cancelResponse = await this.api.post(`datasets/${datasetIdentifier}/train/freeze/cancel`);

            return {
                success: cancelResponse ? cancelResponse.success : false
            };
        } catch (error) {
            throw error;
        }
    }

    public async cancelAnomalyHiFi(datasetIdentifier: string): Promise<JobCancelResponse> {
        try {
            const cancelResponse = await this.api.post(`datasets/${datasetIdentifier}/train/anomalyhifi/cancel`);

            return {
                success: cancelResponse ? cancelResponse.success : false
            };
        } catch (error) {
            throw error;
        }
    }

    public async cancelDetectionTraining(datasetIdentifier: string): Promise<JobCancelResponse> {
        try {
            const cancelResponse = await this.api.post(`datasets/${datasetIdentifier}/train/detection/cancel`);

            return {
                success: cancelResponse ? cancelResponse.success : false
            };
        } catch (error) {
            throw error;
        }
    }

    /**
     * Return a string describing how much remaining work exists for the specified job
     * that is currently in the Processing state
     *
     * @param job The job for which to return the description
     * @param brain The brain associated with the boosting job
     */
    public computeEstimateWithUnitsAndLabel(job: JobResponse, brain?: DatasetBrain | undefined): string {
        const { rawTime, currentStep, totalSteps } = this.computeLatestEstimate(job, brain);

        if (currentStep == -1 || rawTime == undefined || rawTime < 0 || this.stillCalculatingEstimate(totalSteps)) {
            return this.translate.instant('Calculating time remaining...');
        } else if (
            this.isLastStepInJob(currentStep, totalSteps) ||
            (this.isAbovePercent(currentStep, totalSteps, 95) && rawTime < 10)
        ) {
            return this.translate.instant('Finishing up...');
        } else {
            const translateReplacements = {
                estimate: this.formatEstimate(rawTime)
            };

            return this.translate.instant('About [[estimate]] remaining', translateReplacements);
        }
    }

    private isLastStepInJob(curr, total): boolean {
        return curr === total;
    }

    private stillCalculatingEstimate(total): boolean {
        return total === 1;
    }

    /**
     * Returns true if division produces a number above supplied percent
     */
    private isAbovePercent(numerator: number, denominator: number, percent: number): boolean {
        if (!numerator || !denominator || !percent || denominator === 0) {
            return false;
        }

        return (numerator / denominator) * 100 > percent;
    }

    private formatEstimate(estimate: number | undefined): string {
        // Round to the nearest second
        return this.apiUsageProvider.formatUsageUnit(estimate, 'sec', 0, true);
    }

    /**
     * Return the estimated time remaining for the boost job currently runnning for the specified dataset.
     * Return a default estimate if no job is currently running
     *
     * @param job The job for which to return the description
     * @param brain The brain associated with the boosting job
     */
    private computeLatestEstimate(job: JobResponse, brain?: DatasetBrain | undefined): LatestEstimate {
        const errorEstimate = { rawTime: 0, currentStep: -1, totalSteps: 1 };

        if (!job || !brain) {
            return errorEstimate;
        }

        if (job.type == JobType.Retrain) {
            if (!job || !job.current) {
                // Show the mock estimate for the first step.
                return { rawTime: MOCK_BOOST_ESTIMATE_MULTIPLIER * brain.numFrames, currentStep: -1, totalSteps: 1 };
            } else {
                /* A retrain job has three steps - training tasks against
                 * various subsets of the dataset's assets.
                 */
                switch (job.current) {
                    case 0:
                        return {
                            rawTime: MOCK_BOOST_ESTIMATE_MULTIPLIER * brain.numFrames,
                            currentStep: 0,
                            totalSteps: job.total || 0
                        };
                    case 1:
                        return {
                            rawTime: MOCK_BOOST_ESTIMATE_MULTIPLIER * brain.numTrainedPolygons,
                            currentStep: 1,
                            totalSteps: job.current
                        };
                    case 2:
                        return {
                            rawTime: MOCK_BOOST_ESTIMATE_MULTIPLIER * brain.numValidationPolygons,
                            currentStep: 2,
                            totalSteps: job.current
                        };
                    default:
                        return errorEstimate;
                }
            }
        } else {
            if (!job.current || !job.total || !job.dateProcessingStartedSeconds) {
                return errorEstimate;
            }

            // When optimizing, we can estimate time by tracking how long each step takes,
            // and multiplying by the total amount of steps required to complete the job.
            const now = new Date();
            const jobStart = new Date(job.dateProcessingStartedSeconds * 1000);
            const millisecondsPerStep =
                (now.getTime() - jobStart.getTime()) / (job.current == 1 ? job.current : job.current - 1);
            const estimateTime = Math.ceil((millisecondsPerStep * (job.total - job.current)) / 1000);

            return { rawTime: estimateTime, currentStep: job.current, totalSteps: job.total };
        }
    }

    public isBoostJobType(jobType: JobType): boolean {
        return BOOST_JOB_TYPES.includes(jobType);
    }

    public getCancelFailedMessage(uxState: BoostJobUXState, jobType: JobType, isRerunning = false): string {
        let jobTypeString = '';

        switch (jobType) {
            case JobType.Retrain:
                jobTypeString = this.translate.instant('Retraining').toLowerCase();
                break;
            case JobType.Optimize:
                jobTypeString = this.translate.instant('Optimization').toLowerCase();
                break;
            case JobType.Freeze:
            case JobType.AnomalyHiFi:
                jobTypeString = isRerunning
                    ? this.translate.instant('Rebuilding').toLowerCase()
                    : this.translate.instant('Building').toLowerCase();
                break;
            case JobType.Train:
                jobTypeString = isRerunning
                    ? this.translate.instant('Retraining').toLowerCase()
                    : this.translate.instant('Training').toLowerCase();
                break;
        }

        if (uxState !== BoostJobUXState.ERROR && uxState !== BoostJobUXState.DONE) {
            return '';
        }

        const translateReplacements = {
            jobType: jobTypeString,
            resultState:
                uxState === BoostJobUXState.ERROR
                    ? this.translate.instant('Failed').toLowerCase()
                    : this.translate.instant('completed successfully')
        };

        return this.translate.instant(
            'We were unable to complete your cancel request because the [[jobType]] job has already [[resultState]]',
            translateReplacements
        );
    }

    public getComprehensivenessOfOptimizationText(): string {
        return this.translate.instant(
            'Choose the optimal comprehensiveness for your model.<br><br>A quick optimization will evaluate a number of our model options and determine the best fit for your Brain.<br><br>A balanced optimization will evaluate a majority of our model options and determine the best fit for your Brain.<br><br>Finally, a thorough optimization will evaluate every possible model option and determine the best fit for your Brain. A thorough optimization takes the most time. Keep in mind, time estimations are based on the amount of data in your dataset.'
        );
    }
}



// WEBPACK FOOTER //
// ./src/providers/boost.ts